import { createFileRoute } from '@tanstack/react-router';
import { useTranslations } from 'use-intl';
import {
  Header,
  HeaderButton,
  HeaderOrganizationMenu,
  HeaderSidebarTrigger,
} from '@/app/ui/components/header';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/app/ui/components/breadcrumb';
import {
  MainScrollArea,
  MainHeader,
  MainNav,
  MainNavList,
  MainNavItem,
  MainNavLink,
  Main,
} from '@/app/ui/components/main';
import { H1 } from '@/app/ui/components/heading';
import NewEvaluationsList from '@/app/components/features/evaluations/NewEvaluationsList';
import { getProjectDetailsQuery } from '@/app/hooks/queries/useProjects';
import { useGetEvaluationsList } from '@/app/hooks/queries/useEvaluations';
import { use } from 'react';
import { AppContext } from '@/app/appContext';
import { ProjectTypeIcon } from '@/app/components/features/projects/ProjectTypeIcon';
import { ProjectModeEnum } from '@/app/utils/constants';
import { HugeiconsIcon } from '@hugeicons/react';
import {
  PlusSignIcon,
  SettingsIcon,
  SquareLock01Icon,
  NewOfficeIcon,
  UserGroupIcon,
  Building06Icon,
  Clock04Icon,
} from '@hugeicons/core-free-icons';
import { CreateEvaluation } from '@/app/components/features/evaluations/CreateEvaluation';
import { Button } from '@/app/ui/components/button';
import { useSetAtom } from 'jotai';
import { createEvaluationOpenAtom } from '@/app/components/features/evaluations/evaluation/store';
import { OrganizationAvatar } from '@/app/ui/components/organization-avatar';
import { Icons } from '@/app/ui/components/icons';
import { useSuspenseQuery } from '@tanstack/react-query';

export const Route = createFileRoute('/_authed/project/$projectId/_layout/')({
  component: RouteComponent,
});

function RouteComponent() {
  const tGeneral = useTranslations('general');
  const t = useTranslations();
  const { userMeta } = use(AppContext);
  const { projectId } = Route.useParams();
  const organizationName = userMeta?.organizationName || '';
  const { data: project } = useSuspenseQuery(getProjectDetailsQuery(projectId));
  const { data: evaluations } = useGetEvaluationsList(userMeta.organizationId ?? '', projectId);
  const setCreateEvaluationOpenAtom = useSetAtom(createEvaluationOpenAtom);
  const clientName = evaluations?.[0]?.companyName;

  return (
    <>
      <CreateEvaluation />
      <Main>
        <Header>
          <HeaderOrganizationMenu />
          <HeaderSidebarTrigger />
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem className="max-md:hidden">
                <BreadcrumbLink to="/">
                  <HugeiconsIcon icon={Building06Icon} />
                  {t('pages.homepage.breadcrumbs.projects')}
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator className="max-md:hidden" />
              <BreadcrumbItem>
                <BreadcrumbLink to="/project/$projectId" params={{ projectId }}>
                  <ProjectTypeIcon type={project?.type} />
                  {project.name}
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>
                  <Icons.evaluations />
                  {t('pages.homepage.projectsList.evaluations')}
                </BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
          <HeaderButton
            onClick={() => setCreateEvaluationOpenAtom(true)}
            aria-label={t('pages.projectDetails.newEvaluation')}
          >
            <HugeiconsIcon icon={PlusSignIcon} />
          </HeaderButton>
        </Header>
        <MainScrollArea>
          <MainHeader>
            <div>
              <H1>{project.name}</H1>
            </div>
            <Button
              variant={evaluations.length === 0 ? 'secondary' : 'primary'}
              kind="with-icon"
              onClick={() => setCreateEvaluationOpenAtom(true)}
            >
              <HugeiconsIcon icon={PlusSignIcon} />
              {t('pages.projectDetails.newEvaluation')}
            </Button>
          </MainHeader>
          <MainNav>
            <MainNavList>
              {clientName && (
                <MainNavItem className="text-muted items-center whitespace-nowrap px-1.75 h-7 gap-1.5 flex text-sm">
                  {organizationName === clientName ? (
                    <OrganizationAvatar
                      organization={{
                        id: userMeta.organizationId ?? '',
                        name: organizationName ?? '',
                      }}
                      size={24}
                      className="rounded-[calc(var(--radius-md)-0.125rem)]"
                    />
                  ) : (
                    <HugeiconsIcon icon={NewOfficeIcon} />
                  )}
                  {clientName} {organizationName === clientName ? `(${tGeneral(`yourOrg`)})` : null}
                </MainNavItem>
              )}
              {project.mode === ProjectModeEnum.OPEN ? null : (
                <MainNavItem>
                  <MainNavLink to="/project/$projectId/members" params={{ projectId }}>
                    <HugeiconsIcon icon={UserGroupIcon} />
                    {project.numberOfUsers} {t('pages.projectMembers.title')}
                    <span className="flex items-center gap-0">
                      (<span className="pl-px">{t('pages.projectMembers.restricted')}</span>
                      <HugeiconsIcon icon={SquareLock01Icon} className="size-3.5! ml-0.5" />)
                    </span>
                  </MainNavLink>
                </MainNavItem>
              )}
              <MainNavItem>
                <MainNavLink to="/project/$projectId/activity" params={{ projectId }}>
                  <HugeiconsIcon icon={Clock04Icon} />
                  {t('pages.activityLog.title')}
                </MainNavLink>
              </MainNavItem>
              <MainNavItem>
                <MainNavLink to="/project/$projectId/settings" params={{ projectId }}>
                  <HugeiconsIcon icon={SettingsIcon} />
                  {t('pages.projectSettings.title')}
                </MainNavLink>
              </MainNavItem>
            </MainNavList>
          </MainNav>
          <NewEvaluationsList />
        </MainScrollArea>
      </Main>
    </>
  );
}
